import React, { useEffect, useMemo } from "react";
import {
  Box,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { Header } from "./componenets/header";
import {
  accordionCont,
  accordionDetails,
  contentCont,
  loadingCont,
  mainCont,
  mainHeaderCont,
  markButton,
  tabsCont,
  totalAndSearchCont,
  totalCont,
} from "./sxprops";

import { NotificationItem } from "./componenets/notification-item";
import { SearchField } from "../../common/components/search-field";
import { FieldValues, useForm } from "react-hook-form";
import { useGetPagedNotifications } from "./api/hooks";
import { NotificationModel } from "./api/types";
import useNotificationsStore from "./api/store";
import { ConfirmMarkAllReadDialog } from "./componenets/mark-as-read-modal";
import { NotificationType } from "../../common/enums/notification-type";
import { notificationsSteps } from "./componenets/walkthrough-types";
import { notificationsQuizQuestions } from "./componenets/quiz";
import { useModuleWalkthrough } from "../../common/components/walkthroughV3/useModuleWalkthrough";
import WalkthroughTrigger from "../../common/components/walkthroughV3/walkthrough-trigger";

type TabKey = "all" | "unread" | "archive";

const getNotificationTitle = (notification: NotificationModel): string => {
  switch (notification.type) {
    case NotificationType.FacilityUserDeletion:
      return `Access Removed from ${notification.facilityName}`;
    case NotificationType.FailedLoginAttempt:
      return `Suspicious Activity Detected ${notification.facilityName}`;
    case NotificationType.NewSignIn:
      return "New Sign-in Alert";
    case NotificationType.NewAppAccess:
      return "New Application Access Granted";
    default:
      return "";
  }
};

export const Notifications: React.FC = () => {
  const notificationsStore = useNotificationsStore();

  const { walkthroughUI, handleLightbulbClick, isWalkthroughActive } = useModuleWalkthrough({
    steps: notificationsSteps,
    questions: notificationsQuizQuestions,
    storageKey: "notifications-walkthrough",
    trackKey: "Notifications",
    isEnabled: true,
    onNext: (_step, _index) => {},
    onCloseAll: () => {},
    quizTitle: "Notifications Knowledge Check",
    moduleName: "Notifications",
  });

  const { control, resetField, handleSubmit } = useForm({
    defaultValues: {
      query: "",
    },
  });

  const onSubmit = (queryData: FieldValues) => {
    notificationsStore.setPageNumber(1);
    notificationsStore.setSearchText(queryData.query);
  };

  const notifs = useGetPagedNotifications();
  const backendItems: NotificationModel[] = notifs.data?.items ?? [];

  const searchTerm =
    notificationsStore.initialNotificationState.searchText
      ?.toString()
      .trim()
      .toLowerCase() || "";

  const activeFilter = useNotificationsStore(
    (s) => s.initialNotificationState.filter,
  );

  const inActiveTab = React.useCallback(
    (n: NotificationModel) => {
      if (activeFilter === "unread") return !n.isRead && !n.isArchived;
      if (activeFilter === "archive") return n.isArchived;
      return !n.isArchived;
    },
    [activeFilter],
  );

  const tabKey: TabKey = (activeFilter ?? "all") as TabKey;

  const baselineByTabRef = React.useRef<Record<TabKey, NotificationModel[]>>({
    all: [],
    unread: [],
    archive: [],
  });

  React.useEffect(() => {
    if (!searchTerm && backendItems.length > 0) {
      baselineByTabRef.current[tabKey] = backendItems;
    }
  }, [backendItems, searchTerm, tabKey]);

  const pool = React.useMemo(() => {
    if (backendItems.length > 0) return backendItems;
    const tabBaseline = baselineByTabRef.current[tabKey];
    return tabBaseline.length > 0
      ? tabBaseline
      : baselineByTabRef.current.all.filter(inActiveTab);
  }, [backendItems, tabKey, inActiveTab]);

  const titleMatches = React.useMemo(() => {
    if (!searchTerm) return [];
    return pool.filter(
      (n) =>
        inActiveTab(n) &&
        getNotificationTitle(n).toLowerCase().includes(searchTerm),
    );
  }, [pool, inActiveTab, searchTerm]);

  const filteredItems = React.useMemo(() => {
    const map = new Map<string, NotificationModel>();
    backendItems.forEach((n) => map.set(n.id, n));
    titleMatches.forEach((n) => map.set(n.id, n));
    return Array.from(map.values());
  }, [backendItems, titleMatches]);

  useEffect(() => {
    const tab = notificationsStore.tab;
    const desired =
      tab === "unread"
        ? false
        : filteredItems.length > 0 && filteredItems.every((n) => n.isRead);
    if (notificationsStore.isAllMarkedRead !== desired) {
      notificationsStore.setIsAllMarkedRead(desired);
    }
  }, [notificationsStore.tab, filteredItems]);

  const handleTabChange = (_: any, v: TabKey) => {
    notificationsStore.setTab(v);
    notificationsStore.setPageNumber(1);

    if (v === "unread") {
      notificationsStore.setIsAllMarkedRead(false);
    } else if (v === "all" || v === "archive") {
      const allRead =
        filteredItems.length > 0 && filteredItems.every((n) => n.isRead);
      notificationsStore.setIsAllMarkedRead(allRead);
    }

    if (v === "unread") notificationsStore.setFilter("unread");
    else if (v === "archive") notificationsStore.setFilter("archive");
    else notificationsStore.setFilter(undefined);
  };

  const sections = useMemo(() => {
    const dayKey = (d: Date) => {
      const c = new Date(d);
      c.setHours(0, 0, 0, 0);
      return c.getTime();
    };

    const todayKey = dayKey(new Date());
    const oneDay = 86_400_000;
    const yesterdayKey = todayKey - oneDay;
    const lastWeekThreshold = todayKey - 7 * oneDay;
    const lastMonthThreshold = todayKey - 30 * oneDay;

    const map = new Map<string, NotificationModel[]>([
      ["Today", []],
      ["Yesterday", []],
      ["Last Week", []],
      ["Last Month", []],
      ["Older", []],
    ]);

    (filteredItems || []).forEach((notif) => {
      const ts = dayKey(new Date(notif.createdAt));
      let label: string;

      if (ts === todayKey) label = "Today";
      else if (ts === yesterdayKey) label = "Yesterday";
      else if (ts < todayKey && ts >= lastWeekThreshold) label = "Last Week";
      else if (ts < lastWeekThreshold && ts >= lastMonthThreshold)
        label = "Last Month";
      else label = "Older";

      map.get(label)!.push(notif);
    });

    const order = ["Today", "Yesterday", "Last Week", "Last Month", "Older"];
    return order
      .map((k) => [k, map.get(k)!] as [string, NotificationModel[]])
      .filter(([, items]) => items.length > 0);
  }, [filteredItems]);

  const nonEmptySections = sections.filter(
    ([, filteredItems]) => filteredItems.length > 0,
  );
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.only("mobile"));

  const handleMarkAll = () => {
    notificationsStore.setshowMarkReadDialog(true);
    notificationsStore.setNotifsToMark(filteredItems);
  };

  useEffect(() => {
    notificationsStore.setPageNumber(1);
    notificationsStore.setSearchText('');
    resetField("query");
  }, []);

  return (
    <Box sx={mainCont}>
      <Box sx={isWalkthroughActive ? { ...mainHeaderCont, position: "relative" } : mainHeaderCont}>
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
          <Header />
          <WalkthroughTrigger id="notifications-walkthrough-start-button" onClick={handleLightbulbClick} />
        </Box>
        <Box sx={totalAndSearchCont}>
          <Box id="notifications-count" sx={totalCont}>
            <Typography variant="body1" color="error">
              {filteredItems.length}
            </Typography>

            {/* default-colored label */}
            <Typography variant="body1" color="text.primary" sx={{ ml: 1 }}>
              Notifications
            </Typography>
          </Box>

          <Box id="notifications-search-bar" data-testid="notifications-search-bar" sx={{ ml: "auto", width: isMobile ? 300 : 400 }}>
            <SearchField
              testIdPrefix="notifications"
              control={control}
              isFetching={notifs?.isFetching}
              resetField={resetField}
              handleSubmit={handleSubmit}
              onSubmit={onSubmit}
            />
          </Box>
        </Box>
        <Box sx={tabsCont}>
          <Tabs
            id="notifications-tabs"
            value={notificationsStore.tab}
            onChange={handleTabChange}
            textColor="inherit"
            TabIndicatorProps={{
              sx: { backgroundColor: "#FF5F47", height: 3, bottom: 0 },
            }}
            sx={{ ml: -3 }}
          >
            {(["all", "unread", "archive"] as TabKey[]).map((key) => (
              <Tab
                id={`notifications-tab-${key}`}
                key={key}
                value={key}
                label={key.charAt(0).toUpperCase() + key.slice(1)}
                sx={{
                  fontWeight:
                    notificationsStore.tab === key ? "bold" : "normal",
                  color:
                    notificationsStore.tab === key
                      ? "primary.main"
                      : "text.secondary",
                }}
              />
            ))}
          </Tabs>
          <Button
            id="mark-all-read-unread-button"
            onClick={handleMarkAll}
            variant="contained"
            disabled={filteredItems.length === 0}
            sx={markButton}
          >
            {notificationsStore.isAllMarkedRead
              ? "Mark All as Unread"
              : "Mark All as Read"}
          </Button>
        </Box>
      </Box>

      <Box id="notifications-list" sx={contentCont}>
        {notifs.isFetching ? (
          <Box sx={loadingCont}>
            <CircularProgress />
          </Box>
        ) : nonEmptySections.length > 0 ? (
          nonEmptySections.map(([dateLabel, items]) => (
            <Accordion
              key={dateLabel}
              defaultExpanded
              disableGutters
              elevation={0}
              sx={accordionCont}
            >
              <AccordionSummary
                id="notifications-content"
                expandIcon={<ExpandMoreIcon />}
              >
                <Typography color="#6D87AA">{dateLabel}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={accordionDetails}>
                {items.length > 0 ? (
                  items.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                    />
                  ))
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography color="text.secondary">
                      No notifications
                    </Typography>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <Typography color="text.secondary">
              No notifications to show
            </Typography>
          </Box>
        )}
      </Box>
      <ConfirmMarkAllReadDialog />
      {walkthroughUI}
    </Box>
  );
};

export default Notifications;
